import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { LeadsTable } from "@/components/admin/LeadsTable";
import { LeadDetail } from "@/components/admin/LeadDetail";
import { ApiLogs } from "@/components/admin/ApiLogs";
import { SettingsForm } from "@/components/admin/SettingsForm";
import { SignOut } from "@phosphor-icons/react";

const TABS = [
  { key: "leads", label: "Leads" },
  { key: "logs", label: "API Usage" },
  { key: "settings", label: "Settings" },
];

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("leads");
  const [selectedLead, setSelectedLead] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream" data-testid="admin-loading">
        <p className="font-heading text-lg font-bold text-soot">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return <AdminLogin onLogin={setUser} />;
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-black/5 bg-cream/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="rounded-full bg-ink px-3 py-1 font-heading text-xs font-bold uppercase tracking-[0.15em] text-cream">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-soot sm:block">{user.email}</span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-1.5 text-sm font-semibold text-soot transition-colors hover:text-ink"
            >
              <SignOut size={16} weight="bold" />
              Log out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8 sm:px-8">
        <nav className="mb-8 flex gap-2" aria-label="Admin sections">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => {
                setTab(t.key);
                setSelectedLead(null);
              }}
              className={`rounded-full px-5 py-2 font-heading text-sm font-bold transition-colors duration-300 ${
                tab === t.key ? "bg-pine text-cream" : "bg-pine-soft/60 text-soot hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {tab === "leads" &&
          (selectedLead ? (
            <LeadDetail
              leadId={selectedLead}
              onBack={() => {
                setSelectedLead(null);
                setRefreshKey((k) => k + 1);
              }}
            />
          ) : (
            <LeadsTable key={refreshKey} onSelect={setSelectedLead} />
          ))}
        {tab === "logs" && <ApiLogs />}
        {tab === "settings" && <SettingsForm />}
      </div>
    </div>
  );
}
